import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, ChevronDownIcon, ZapIcon, HistoryIcon, SendIcon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
interface RaastRegisterProps {
  onBack: () => void;
  onHome: () => void;
  // Added to support the shortcut grid on the empty state (Merchant History, etc.)
  // Wire this the same way RaastHome's / RaastRelinkDelink's onNavigate is wired.
  onNavigate?: (view: string) => void;
}
// 'empty' restored — this is the landing state before a RAAST ID is registered,
// now redesigned per the new screenshot (card + Register Now + shortcut grid).
type Step = 'empty' | 'form' | 'otp' | 'success';
// Shared button style: height 55px, width 280px, radius 8px, orange fill
const PRIMARY_BUTTON_CLASS = 'h-[55px] w-[280px] mx-auto rounded-lg font-bold text-[16px] bg-[#F37024] text-white shadow-md flex items-center justify-center disabled:opacity-50';
const shortcuts = [{
  id: 'history',
  title: 'Merchant History',
  desc: 'View your RAAST merchant payments',
  icon: HistoryIcon,
  view: 'history'
}, {
  id: 'requests',
  title: 'Payment Requests',
  desc: 'View and manage RTP requests',
  icon: CheckCircle2Icon,
  view: 'requests'
}, {
  id: 'pay_merchant',
  title: 'Pay to Merchant',
  desc: 'Scan a QR or enter a till number',
  icon: SendIcon,
  view: 'qr_till'
}, {
  id: 'pay_person',
  title: 'Pay to Person',
  desc: 'Scan a QR or enter a till number',
  icon: SendIcon,
  view: 'pay_person'
}];
export function RaastRegister({
  onBack,
  onHome,
  onNavigate
}: RaastRegisterProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('empty');
  const [direction, setDirection] = useState(1);
  // Form State — account is the only user-editable field.
  // Title and mobile number are derived (autofetched) from the selected account.
  const [selectedAccount, setSelectedAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  // NOTE: PAY_FROM_ACCOUNTS (defined in RaastShared) currently only has
  // { id, label, number }. For autofetch to show real data, add `title` and
  // `mobileNumber` fields to each account there. Falling back below so this
  // still renders sensibly until that shape is updated.
  const accountTitle = (selectedAccount as any).title ?? 'KASHISH AHMED';
  const accountMobileNumber = (selectedAccount as any).mobileNumber ?? '03001234567';
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`raast-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`raast-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  // Canonical header — back+label (16px regular) on left, icon-only maroon home on right
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2621" data-mp="wqc2gkv1cnck">
      <button onClick={onBackClick} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${textPri}`} aria-label="Back" data-id="element-2622" data-mp="wohesysdqh4s">
        <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2623" data-mp="nqxblkayiarj" />
        <span className={`text-[16px] font-normal ${textPri}`} data-id="element-2624" data-mp="iqsupt64ze0s">{title}</span>
      </button>
      <div className="flex-1" data-id="element-2625" data-mp="it5lmvtvgypz" />
      <button onClick={onHome} className={`p-2 -mr-2 ${isDark || isPlatinum ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-2626" data-mp="1fhldwzod9m6s">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2627" data-mp="1bhug4c0fa5sr" />
      </button>
    </div>;
  // Empty state — card (logo + heading + description + Register Now) + shortcut grid
  const renderEmpty = () => <motion.div key="empty" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2628" data-mp="11nu9g5phsshx">
      {renderHeader('Raast', onBack)}
      <div className="px-6 flex-1 flex flex-col gap-4 pb-8" data-id="element-2629" data-mp="21r1sw81s79f4">
        <div className={`rounded-lg p-6 border shadow-sm flex flex-col items-center text-center ${cardBg} ${borderClr}`} data-id="element-2630" data-mp="26o21apm3so9e">
          {/* Placeholder logo mark — swap for the actual Raast logo asset */}
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark || isPlatinum ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2631" data-mp="2fqb7526fftgg">
            <ZapIcon className="w-8 h-8 text-[#F37024]" fill="currentColor" data-id="element-2632" data-mp="2pmwf9wdqtkoi" />
          </div>
          <h2 className={`text-[16px] font-medium mb-2 ${textPri}`} data-id="element-2633" data-mp="2ueei6pzbtvc4">
            No RAAST ID Found
          </h2>
          <p className={`text-[13px] font-normal mb-6 ${textSec}`} data-id="element-2634" data-mp="2zdip19ys3gza">
            You haven't registered a RAAST ID yet. Link your mobile number to
            your account to receive payments instantly.
          </p>
          {/* Full width per the screenshot, not the 280px spec — same open flag as RelinkDelink's CTA */}
          <button onClick={() => navigateTo('form')} className="w-full h-[55px] rounded-lg bg-[#F37024] text-white font-bold text-[16px] shadow-md" data-id="element-2635" data-mp="18chdq5i9uhay">
            Register Now
          </button>
        </div>

        {/* Shortcut grid — 2x2, radius 8px per the tile rule */}
        <div className="grid grid-cols-2 gap-4" data-id="element-2636" data-mp="1d97s8oefh4u9">
          {shortcuts.map(s => <button key={s.id} onClick={() => onNavigate?.(s.view)} className={`text-left rounded-lg p-4 border ${borderClr} ${isDark ? 'bg-[#2D1810]' : 'bg-[#FDF3ED]'}`} data-id="element-2637" data-mp="1ryca24hb971u">
              <s.icon className="w-6 h-6 text-[#F37024] mb-3" strokeWidth={2} data-id="element-2638" data-mp="20zk58doj5rrp" />
              <p className={`text-[14px] font-medium mb-1 ${textPri}`} data-id="element-2639" data-mp="1w1s4qv9tkxbw">
                {s.title}
              </p>
              <p className={`text-[11px] font-normal ${textSec}`} data-id="element-2640" data-mp="6mklq9h14df5">{s.desc}</p>
            </button>)}
        </div>
      </div>
    </motion.div>;
  // Form — sequence is now: Account selection -> Account Title (autofetched) -> Mobile Number (autofetched)
  const renderForm = () => <motion.div key="form" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2641" data-mp="2i8w80882l6fm">
      {renderHeader('Register RAAST ID', () => navigateTo('empty', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2642" data-mp="1uiigcited9lk">
        <div className="space-y-6 mb-auto" data-id="element-2643" data-mp="1zjpmkdfw60q4">
          {/* Account selection — the only editable field */}
          <div className="relative" data-id="element-2644" data-mp="1lk6qij0cufu6">
            <label className={`text-[13px] font-medium mb-2 block ${textPri}`} data-id="element-2645" data-mp="1ll82qqt3x8dh">
              Link to Account
            </label>
            <button onClick={() => setShowAccountDropdown(!showAccountDropdown)} className={`w-full rounded-lg p-4 flex items-center justify-between border ${cardBg} ${borderClr}`} data-id="element-2646" data-mp="3fxg34jo76hgn">
              <div className="text-left" data-id="element-2647" data-mp="27q7nfzvdfhk7">
                <p className={`text-[14px] font-medium ${textPri}`} data-id="element-2648" data-mp="27owg5q2pvroc">
                  {selectedAccount.label}
                </p>
                <p className={`text-[13px] font-medium ${textSec}`} data-id="element-2649" data-mp="1xzv0y23zxebf">
                  {selectedAccount.number}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-2650" data-mp="1t00ce57p10tj" />
            </button>
            <AnimatePresence data-id="element-2651" data-mp="1p07ydczj62gs">
              {showAccountDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className={`absolute top-full left-0 right-0 mt-2 rounded-lg shadow-xl overflow-hidden border z-10 ${cardBg} ${borderClr}`} data-id="element-2652" data-mp="1k0d7h9fr9cvq">
                  {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                setSelectedAccount(acc);
                setShowAccountDropdown(false);
              }} className={`w-full text-left p-4 border-b last:border-0 ${borderClr} ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2653" data-mp="3cuuyye89959a">
                      <p className={`text-[13px] font-medium ${textPri}`} data-id="element-2654" data-mp="2u069kzwzl3f8">
                        {acc.label}
                      </p>
                      <p className={`text-[13px] font-medium ${textSec}`} data-id="element-2655" data-mp="32y8gtx7bcsay">
                        {acc.number}
                      </p>
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Account Title — autofetched, greyed (not orange) */}
          <div data-id="element-2656" data-mp="n62h0mb4tnb9">
            <label className={`text-[13px] font-medium mb-2 block ${textPri}`} data-id="element-2657" data-mp="n9qbbnoeidr1">
              Account Title
            </label>
            <input type="text" value={accountTitle} disabled className={`w-full rounded-lg py-4 px-4 text-[14px] font-medium border cursor-not-allowed ${isDark || isPlatinum ? 'bg-[#2A2A2C] text-[#9A9A9E] border-[rgba(255,255,255,0.08)]' : 'bg-[#F2F2F2] text-[#818285] border-[#E5E5E5]'}`} data-id="element-2658" data-mp="1upwzfy9hgoep" />
          </div>

          {/* Mobile Number — autofetched, greyed (not orange) */}
          <div data-id="element-2659" data-mp="1bs3xay6ooiid">
            <label className={`text-[13px] font-medium mb-2 block ${textPri}`} data-id="element-2660" data-mp="1brkpwbentcil">
              Mobile Number / RAAST ID
            </label>
            <input type="tel" value={accountMobileNumber} disabled className={`w-full rounded-lg py-4 px-4 text-[14px] font-medium border cursor-not-allowed ${isDark || isPlatinum ? 'bg-[#2A2A2C] text-[#9A9A9E] border-[rgba(255,255,255,0.08)]' : 'bg-[#F2F2F2] text-[#818285] border-[#E5E5E5]'}`} data-id="element-2661" data-mp="1krqzhm78lsch" />
          </div>
        </div>

        {/* Goes straight to OTP — review step removed */}
        <button onClick={() => navigateTo('otp')} className={`${PRIMARY_BUTTON_CLASS} mt-6`} data-id="element-2662" data-mp="31cjbkb3zah98">
          Next
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2663" data-mp="2shljt7ckhajh">
      {renderHeader('Enter OTP', () => navigateTo('form', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2664" data-mp="2dmp3e6ultgvb">
        <div className="mb-8 text-center" data-id="element-2665" data-mp="29lbz4175tlqi">
          <p className={`text-[14px] ${textSec}`} data-id="element-2666" data-mp="29mmzrs5b6dde">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2667" data-mp="7sxhz4cfwlb5">
          {otp.map((digit, i) => <input key={i} id={`raast-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-lg text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2668" data-mp="21bo0bu7nawbw" data-mpi={i} />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2669" data-mp="2b2iqh9o0ig2m">
          {timer > 0 ? <p className={`text-[14px] font-normal ${textSec}`} data-id="element-2670" data-mp="2g12b96qvdlte">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> :
        // Kept as a text-style link, not a full 280px CTA — see note above.
        <button onClick={() => setTimer(30)} className="text-[13px] font-medium text-[#F37024]" data-id="element-2671" data-mp="14mylkcrbeju5">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  // Success — bottom-sheet modal over a dimmed "Review Details / Confirm Registration"
  // underlay, matching the reference screenshot.
  const renderSuccess = () => <motion.div key="success" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className={`w-full min-h-screen flex flex-col relative overflow-hidden ${bgStyle}`} data-id="element-2672" data-mp="114ixd54v9lua">
      {/* Dimmed review underlay */}
      <div className="flex-1 flex flex-col pointer-events-none" data-id="element-2673" data-mp="n4qaxm0dhrs4">
        {renderHeader('Review Details', () => {})}
        <div className="px-6" data-id="element-2674" data-mp="icgnqdqn75tj">
          <div className={`h-px w-full ${borderClr} mb-5`} data-id="element-2675" data-mp="ic6xcp1oqwnb" />
          <h3 className={`text-[16px] font-bold text-center mb-6 ${textPri}`} data-id="element-2676" data-mp="3qsjn7w7lpo6s">
            Confirm Registration
          </h3>
          <div className="space-y-4" data-id="element-2677" data-mp="3ltp1alec7jig">
            <div className="flex justify-between" data-id="element-2678" data-mp="2p2tredfr2r7u">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2679" data-mp="2p44v6d973yyy">
                Account Title
              </span>
              <span className={`text-[13px] font-bold ${textPri}`} data-id="element-2680" data-mp="2tztv4hp4nhvs">
                {accountTitle}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2681" data-mp="32tzyaulczr4r">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2682" data-mp="32vjb2b7t3k1s">
                RAAST ID
              </span>
              <span className={`text-[13px] font-bold ${textPri}`} data-id="element-2683" data-mp="37vfk4ovd21o7">
                {accountMobileNumber}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2684" data-mp="3hr93os7hyvfh">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2685" data-mp="3hpedozhdcd4j">
                Linked Account
              </span>
              <span className={`text-[13px] font-bold ${textPri}`} data-id="element-2686" data-mp="1ndrb2qs8j8dl">
                {selectedAccount.number.slice(-4)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrim */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="absolute inset-0 bg-black/50 z-20" data-id="element-2687" data-mp="1jw3iuv3aqa4j" />

      {/* Bottom sheet */}
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} transition={{
      type: 'spring',
      damping: 28,
      stiffness: 300
    }} className={`absolute bottom-0 left-0 right-0 z-30 rounded-t-[28px] px-6 pt-8 pb-8 flex flex-col items-center ${cardBg}`} data-id="element-2688" data-mp="1a6sflp33bzok">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 15,
        delay: 0.1
      }} className="w-20 h-20 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-5" data-id="element-2689" data-mp="1ttq3zzb6uxwy">
          <div className="w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center shadow-lg shadow-[#22C55E]/30" data-id="element-2690" data-mp="ddwxta5n6q28">
            <CheckCircle2Icon className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-2691" data-mp="eukm281i70kd" />
          </div>
        </motion.div>
        <h2 className={`text-[24px] font-bold mb-2 text-center ${textPri}`} data-id="element-2692" data-mp="5uxhxehe3gnm">
          Your RAAST ID is registered
        </h2>
        <p className={`text-[14px] text-center mb-8 ${textSec}`} data-id="element-2693" data-mp="9vr5g6qx6tfz">
          You can now send and receive money instantly using your RAAST ID.
        </p>
        <button onClick={onHome} className="h-[55px] w-[280px] rounded-lg font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2694" data-mp="x5ouh50dohu">
          Done
        </button>
      </motion.div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2695" data-mp="3ncqfumqndy85">
      {step === 'empty' && renderEmpty()}
      {step === 'form' && renderForm()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}